import * as gsap from "gsap";
import * as HLF from "html-living-framework";
import {Application} from "../Application";
import {Callback} from "../core/Reflection";
import {Once} from "../core/Signal";
import {Subscriber} from "../core/Subscriber";
import {CaptionToken} from "../data/CaptionToken";
import * as Topics from "../data/Topics";
import {CaptionManager} from "./CaptionManager";

export enum PlayMode {
    Append = "append",
    Override = "override",
    New = "new",
}

export interface PlayOptions {
    skipCC?: boolean;
    mode?: PlayMode;
    priority?: number;
    cb?: Callback;
}

interface QueueItem extends PlayOptions {
    key: string;
    delay?: number;
}

export class VoiceOverManager extends Subscriber {

    static get instance() {
        return this._instance || (this._instance = new VoiceOverManager());
    }

    private get activeVO(): HLF.HowlerTrack | undefined {
        if (this._queue.length > 0 && this._queue[0].key) {
            return Application.instance.audio.getAudioTrack(this._queue[0].key, HLF.AudioCategory.VO) as HLF.HowlerTrack;
        }
        return undefined;
    }

    private static _instance: VoiceOverManager;

    private readonly _queue: QueueItem[] = [];
    private _activeTimeout?: gsap.TweenMax;

    constructor() {
        super();

        this.subscribe(HLF.GAME_PAUSED, this.onPause.bind(this));
        this.subscribe(HLF.GAME_UNPAUSED, this.onResume.bind(this));
    }

    public playVO(key: string | Array<string | number>, mode?: PlayMode | Callback | PlayOptions, cb?: Callback) {
        if (!Array.isArray(key)) {
            key = [key];
        }
        let skipCC = false;
        let priority = 0;
        if (typeof mode === "function") {
            cb = mode;
        }
        if (typeof mode === "object") {
            skipCC = mode.skipCC === true;
            priority = mode.priority || 0;
            cb = mode.cb;
            mode = mode.mode;
        }
        if (!(typeof mode === "string")) {
            mode = PlayMode.Override;
        }
        if (this._queue.length === 0 ||
            (mode === PlayMode.Override && priority >= this._queue[0].priority!) ||
            (mode === PlayMode.New && priority > this._queue[0].priority!)) {
            this.stopVO();
            this.addToQueue(key, cb, skipCC, priority);
            this.playNext();
        } else if (mode === PlayMode.Append) {
            this.addToQueue(key, cb, skipCC, priority);
        } else if (cb) {
            cb();
        }
    }

    public stopVO(): void {
        const activeVO: HLF.HowlerTrack | undefined = this.activeVO;
        const activeItem = this._queue[0];
        this._queue.splice(0, this._queue.length);
        if (activeVO) {
            activeVO.off(HLF.HowlerUtils.Events.END);
            switch (activeVO.getSource().state()) {
                case HLF.HowlerUtils.State.LOADED:
                // if (activeVO.isPlaying()) {
                //     activeVO.fadeTo(0, 250);
                //     PubSub.publishSync(Topics.STOP_CAPTION, undefined);
                // }
                // break;
                case HLF.HowlerUtils.State.LOADING:
                    activeVO.stop();
                    break;
            }
            if (activeItem && !activeItem.skipCC) {
                PubSub.publishSync(Topics.STOP_CAPTION, new CaptionToken(activeItem.key));
            }
        }
        if (this._activeTimeout) {
            this._activeTimeout.kill();
            this._activeTimeout = undefined;
        }
    }

    private addToQueue(keys: Array<string | number>, cb?: Callback, skipCC?: boolean, priority?: number) {
        for (let i = 0; i < keys.length; i++) {
            const key = keys[i];
            this._queue.push({
                key: typeof key === "string" ? key : "",
                delay: typeof key === "number" ? key : undefined,
                cb: i === keys.length - 1 ? cb : undefined,
                skipCC,
                priority,
            });
        }
        const toLoad = keys.filter((it) => typeof it === "string") as string[];
        Application.instance.audio.load(toLoad, HLF.AudioCategory.VO);
    }

    private playNext() {
        if (this._queue.length > 0) {
            const item = this._queue[0];
            if (item.delay) {
                if (this._activeTimeout) {
                    this._activeTimeout.kill();
                }
                this._activeTimeout = gsap.TweenMax.delayedCall(item.delay, () => {
                    this._activeTimeout = undefined;
                    this._queue.shift();
                    if (item.cb) {
                        item.cb();
                    }
                    this.playNext();
                });
            } else {
                Application.instance.audio.load(item.key, HLF.AudioCategory.VO, () => {
                    if (this._queue[0] !== item) {
                        return;
                    }
                    // AudioChannel.BGM.volume = 0.5;
                    Application.instance.audio.play(item.key, 0.8, false, HLF.AudioCategory.VO);
                    if (!item.skipCC) {
                        PubSub.publishSync(Topics.PLAY_CAPTION, new CaptionToken(item.key));
                    }
                    const onEnd = Once(() => {
                        // AudioChannel.BGM.volume = 1;
                        if (this._queue[0] !== item) {
                            return;
                        }
                        this._queue.shift();
                        if (!item.skipCC) {
                            PubSub.publishSync(Topics.STOP_CAPTION, new CaptionToken(item.key));
                        }
                        if (this._activeTimeout) {
                            this._activeTimeout.kill();
                            this._activeTimeout = undefined;
                        }
                        if (item.cb) {
                            item.cb();
                        }
                        this.playNext();
                    });
                    if (this.activeVO) {
                        // this.activeVO.once(HLF.HowlerUtils.Events.END, onEnd);

                        // Some devices cannot recognize VO duration
                        const duration = CaptionManager.instance.getDuration(item.key);
                        const vo = this.activeVO;
                        if (!isNaN(duration)) {
                            this._activeTimeout = gsap.TweenMax.delayedCall(duration, () => {
                                this._activeTimeout = undefined;
                                if (vo === this.activeVO) {
                                    onEnd();
                                }
                            });
                        }
                    } else {
                        onEnd();
                    }
                });
            }
        }
    }

    private onPause() {
        if (this.activeVO !== undefined && this.activeVO.getTimePos() > 0) {
            this.activeVO.pause();
        }
        if (this._activeTimeout) {
            this._activeTimeout.pause();
        }
    }

    private onResume() {
        if (this.activeVO !== undefined && this.activeVO.getTimePos() > 0) {
            this.activeVO.play();
        }
        if (this._activeTimeout) {
            this._activeTimeout.resume();
        }
    }
}
